import React, { useEffect, useState } from 'react';
import { useColyseusMessages } from '../hooks/useColyseusMessages';
import { COLORS, FONTS } from '../theme';

/**
 * DrainOverlay — full-screen modal shown when the server initiates a drain.
 *
 * Listens for the `serverDraining` message broadcast by the server-side
 * DrainOrchestrator (via `src/server/routes/admin.routes.ts:POST
 * /api/admin/drain`). Displays a countdown + the operator-supplied
 * message + a "your progress is saved" reassurance. After grace expires,
 * the server gracefully shuts down — clients see the WebSocket close
 * with the draining context already on screen, so the disconnect feels
 * intentional rather than crashy.
 *
 * Reconnect-on-disconnect substrate prep:
 *   When the server comes back up post-drain, the existing connect
 *   path will reconnect naturally. Future work (h2000's own arc) wires
 *   Colyseus `allowReconnection` into the room lifecycle so the SAME
 *   sessionId is preserved across the drain → restart cycle (no
 *   "different player" effect on reconnect). This overlay is the UI
 *   substrate that arc plugs into — no overlay code change needed.
 *
 * Subscription pattern mirrors DeathOverlay + DuelOverlays — poll-bind
 * to the room every 500ms so it survives reconnects.
 *
 * Mounted from src/main.tsx alongside DeathOverlay + DuelOverlays.
 *
 * data-testid: 'drain-overlay' for multibrowser smoke (see
 * helix/Helix2000/qa/multibrowser/h2000-drain.mjs).
 */

interface DrainState {
  message: string;
  graceExpiresAt: number;
}

export const DrainOverlay: React.FC = () => {
  const [drain, setDrain] = useState<DrainState | null>(null);
  const [now, setNow] = useState<number>(Date.now());

  useColyseusMessages({
    serverDraining: (msg: any) => {
      const graceMs = typeof msg?.graceMs === 'number' ? msg.graceMs : 60_000;
      const graceExpiresAt =
        typeof msg?.graceExpiresAt === 'number'
          ? msg.graceExpiresAt
          : Date.now() + graceMs;
      setDrain({
        message:
          typeof msg?.message === 'string' && msg.message
            ? msg.message
            : 'Server restarting — your progress is saved.',
        graceExpiresAt,
      });
    },
  });

  // Countdown ticker — runs only while the overlay shows (B7: an always-on
  // 4 Hz ticker re-rendered this hidden component all game long).
  useEffect(() => {
    if (!drain) return undefined;
    setNow(Date.now());
    const tickInterval = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(tickInterval);
  }, [drain]);

  if (!drain) return null;

  const remainingMs = Math.max(0, drain.graceExpiresAt - now);
  const remainingSec = Math.ceil(remainingMs / 1000);

  return (
    <div
      data-testid="drain-overlay"
      data-drain-remaining-sec={remainingSec}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(20, 20, 30, 0.85)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ecf0f1',
        fontFamily: 'Inter, sans-serif',
        backdropFilter: 'blur(4px)',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          padding: '32px 48px',
          background: 'rgba(0, 0, 0, 0.6)',
          border: `2px solid ${COLORS.warning}`,
          borderRadius: 12,
          maxWidth: 560,
          textAlign: 'center',
          boxShadow: '0 0 40px rgba(241, 196, 15, 0.25)',
        }}
      >
        <div
          style={{
            fontFamily: FONTS.display, // "moment" title (§3)
            fontSize: 28,
            color: COLORS.warning,
            letterSpacing: '0.1em',
            marginBottom: 16,
            fontWeight: 'bold',
          }}
        >
          🛑 SERVER MAINTENANCE
        </div>
        <div
          data-testid="drain-message"
          style={{ fontSize: 16, lineHeight: 1.5, marginBottom: 24, color: '#ecf0f1' }}
        >
          {drain.message}
        </div>
        <div
          data-testid="drain-countdown"
          style={{
            fontSize: 48,
            fontWeight: 'bold',
            color: remainingSec > 10 ? '#2ecc71' : remainingSec > 5 ? '#f39c12' : '#e74c3c',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {remainingSec}s
        </div>
        <div style={{ fontSize: 12, color: '#95a5a6', marginTop: 16 }}>
          The connection will close when the timer reaches zero. Reconnect after
          a moment to resume.
        </div>
      </div>
    </div>
  );
};
